import { Message } from 'sharedUi/Message';
import { Input } from 'sharedUi/Input';
import MpinInput from '../components/forms/mpin-input';
import { Button } from 'sharedUi/Button';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearError, login } from '../features/auth/authSlice';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { projectsService, type PublicProjectCode } from '../services/projects.service';
// FE icon asset used for the floating mark in the project-picker hero.
import projectsIcon from '../assets/icons/projects.png';

// A project image. `fit="cover"` fills its container richly (banner cards);
// `fit="contain"` shows the whole image (loaders) so a logo is never cropped.
// Falls back to a tinted tile with the project's initial when there is no image or it
// fails to load.
const ProjectImage = ({
  imageUrl,
  name,
  appCode,
  className = '',
  initialSize = 28,
  fit = 'contain',
  fallbackGradient = 'linear-gradient(135deg, #3b82f6, #2563eb)',
}: {
  imageUrl?: string | null;
  name: string;
  appCode?: string | null;
  className?: string;
  initialSize?: number;
  fit?: 'cover' | 'contain';
  fallbackGradient?: string;
}) => {
  const [errored, setErrored] = useState(false);
  if (imageUrl && !errored) {
    return (
      <img
        src={imageUrl}
        alt={name}
        className={`${fit === 'cover' ? 'object-cover' : 'object-contain p-1'} ${className}`}
        onError={() => setErrored(true)}
      />
    );
  }
  return (
    <div
      className={`flex items-center justify-center text-white font-semibold ${className}`}
      style={{ background: fallbackGradient, fontSize: initialSize }}
    >
      {(name || appCode || '?').charAt(0).toUpperCase()}
    </div>
  );
};

// Crisp gradient icon tiles for the project cards — raster project photos read as
// muddy thumbnails at icon size, so each project gets a clean colored initial tile
// (a distinct hue per card, like the reference dashboard's app icons).
const ICON_GRADIENTS = [
  'linear-gradient(135deg, #3b82f6, #2563eb)', // blue
  'linear-gradient(135deg, #8b5cf6, #6d28d9)', // violet
  'linear-gradient(135deg, #ec4899, #be185d)', // pink
  'linear-gradient(135deg, #f59e0b, #d97706)', // amber
  'linear-gradient(135deg, #10b981, #059669)', // emerald
  'linear-gradient(135deg, #06b6d4, #0891b2)', // cyan
];

const Login = () => {
  const [loginId, setLoginId] = useState('');
  const [mpin, setMpin] = useState('');
  // Full public project list (pre-auth), keyed later by appCode to enrich the
  // ambiguous-email chooser with each project's image + description. No up-front
  // picker: a single project auto-pins; the chooser only appears AFTER correct
  // credentials match more than one project.
  const [publicProjects, setPublicProjects] = useState<PublicProjectCode[]>([]);
  // Set only for an ambiguous email login (same email+mpin in several projects): the
  // matched projects to choose from before completing sign-in.
  const [projectChoices, setProjectChoices] = useState<
    { appId: string | null; appCode?: string | null; name: string }[] | null
  >(null);
  // The project we're completing sign-in into (chooser path) — drives a loading view so
  // the form doesn't flash while the login + role/permission resolution runs.
  const [signingInto, setSigningInto] = useState<{ appCode?: string | null; name: string } | null>(null);
  // Free-text filter for the chooser — only surfaced when there are many projects.
  const [projectFilter, setProjectFilter] = useState('');
  // Brand art, both from public/: -logo-full.png is the lockup (mark + wordmark)
  // used in the header bar; -logo.png is the square mark alone, used on the tile.
  // Falls back to the shield mark + name if the file is absent/broken.
  const [brandLogoBroken, setBrandLogoBroken] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // Load the public project list ONLY to enrich the ambiguous-email picker (image,
  // description, name). It no longer drives the appCode sent at login — the initial
  // login sends none and the backend resolves the project from the credentials.
  useEffect(() => {
    projectsService
      .getPublicCodes()
      .then((codes) => setPublicProjects(codes))
      .catch(() => {});
  }, []);

  const submit = async (appCodeOverride?: string) => {
    const isEmail = loginId.includes('@');
    const res = await dispatch(login({
      ...(isEmail ? { email: loginId } : { userId: loginId }),
      // BE expects mpin as a STRING (used directly for bcrypt verification).
      mpin: mpin.trim(),
      // appCode is sent ONLY when a project was explicitly chosen from the
      // ambiguous-email picker. The initial login sends none — the backend resolves
      // the project from the credentials (unique userId, or email+mpin match).
      ...(appCodeOverride ? { appCode: appCodeOverride } : {}),
    }));
    if (login.fulfilled.match(res) && (res.payload as any)?.projectSelectionRequired) {
      // Ambiguous email → the BE returned matching projects; show the chooser.
      setSigningInto(null);
      setProjectChoices((res.payload as any).projects || []);
    } else if (!login.fulfilled.match(res)) {
      // Rejected (bad mpin, blocked, etc.) → drop the loading view so the form + error show.
      setSigningInto(null);
    }
    // Fulfilled + signed in → the isAuthenticated effect navigates away; keep the loading
    // view until this screen unmounts so the form never flashes.
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setProjectChoices(null);
    submit();
  };

  // Pick a project in the ambiguous-email case → show a loading view and re-submit login
  // with its appCode, which resolves to exactly one account and completes sign-in.
  const chooseProject = (project: { appCode?: string | null; name: string }) => {
    if (!project.appCode) return;
    setProjectChoices(null);
    setSigningInto(project);
    submit(project.appCode);
  };

  const features = [
    { icon: 'pi-lock', label: 'Secure Access' },
    { icon: 'pi-users', label: 'Role Based' },
    { icon: 'pi-chart-bar', label: 'Analytics' },
  ];

  // Project-picker takeover: hide the branding pane and let the picker use the whole
  // page (wide, multi-column) instead of the cramped half-width form panel.
  const picking = !!projectChoices && !signingInto;
  // The whole project flow — picking a project AND the "signing in to it" loader — runs
  // full-page, so the loader doesn't snap back to the cramped half-page split layout.
  const fullBleed = picking || !!signingInto;
  // The chosen project's public metadata (image/description) for the loader animation.
  const signingMeta = signingInto?.appCode
    ? publicProjects.find((pp) => pp.appCode === signingInto.appCode)
    : undefined;

  return (
    <div className="min-h-screen flex" data-testid="SCR-Auth-Login">
      {/* Scoped animations */}
      <style>{`
        @keyframes lg-fade-up { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes lg-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-18px); } }
        .lg-enter { animation: lg-fade-up .6s cubic-bezier(.22,1,.36,1) both; }
        .lg-enter-1 { animation-delay: .05s; }
        .lg-enter-2 { animation-delay: .12s; }
        .lg-enter-3 { animation-delay: .19s; }
        .lg-float { animation: lg-float 7s ease-in-out infinite; }
        .lg-float-slow { animation: lg-float 11s ease-in-out infinite; }
        .login-submit-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 16px 34px -8px rgba(110,79,176,0.55) !important; }
        .login-submit-btn:active:not(:disabled) { transform: translateY(0); }
      `}</style>

      {/* Left Panel - Branding (hidden during the project flow → full-page takeover) */}
      <div className={`${fullBleed ? 'hidden' : 'hidden lg:flex'} lg:w-1/2 relative overflow-hidden`}
        style={{
          /* Brand hero sweep — defined once in index.css (the one theme file). */
          background: 'var(--brand-hero-gradient)',
        }}
      >
        {/* Textured grid overlay */}
        <div className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
            backgroundSize: '44px 44px',
            maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 78%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 78%)',
          }}
        />
        {/* Animated glow orbs */}
        <div className="absolute inset-0 opacity-60">
          <div className="lg-float absolute top-16 left-8 w-72 h-72 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.45) 0%, transparent 70%)' }} />
          <div className="lg-float-slow absolute bottom-16 right-6 w-96 h-96 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.35) 0%, transparent 70%)' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(37,99,235,0.22) 0%, transparent 70%)' }} />
        </div>

        <div className="relative z-10 flex flex-col justify-center items-center w-full px-12 text-white">
          <div className="lg-enter w-24 h-24 rounded-3xl flex items-center justify-center mb-8 shadow-2xl ring-1 ring-white/20 overflow-hidden bg-white"
            style={{ boxShadow: '0 20px 45px -12px rgba(18,39,92,0.7), 0 0 0 8px rgba(120,72,168,0.10)' }}
          >
            <img src="/4scoreastrum-logo.png" alt="4sCoreASTRUM" className="w-full h-full object-cover"
              onError={(e) => { (e.currentTarget.style.display = 'none'); }} />
          </div>
          <h1 className="lg-enter lg-enter-1 text-4xl font-bold tracking-tight mb-4 text-center bg-gradient-to-r from-blue-300 via-indigo-300 to-violet-400 bg-clip-text text-transparent">
            4sCoreASTRUM
          </h1>
          <p className="lg-enter lg-enter-2 text-lg text-blue-200/90 text-center max-w-sm leading-relaxed">
            Secure. Smart. Streamlined. System.
          </p>

          <div className="lg-enter lg-enter-3 mt-16 grid grid-cols-3 gap-5 w-full max-w-md">
            {features.map((f) => (
              <div key={f.label}
                className="group rounded-2xl px-3 py-5 text-center border border-white/10 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-white/25"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
              >
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center transition-colors duration-300 group-hover:bg-blue-500/25"
                  style={{ backgroundColor: 'rgba(255,255,255,0.10)' }}>
                  <i className={`pi ${f.icon} text-blue-200`} style={{ fontSize: '1.25rem' }} />
                </div>
                <p className="text-xs text-blue-200/90 font-medium">{f.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form (goes full-width while picking a project) */}
      <div className={`w-full ${fullBleed ? 'lg:w-full bg-gradient-to-br from-slate-50 via-white to-blue-50/50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950' : 'lg:w-1/2 bg-white dark:bg-gray-900'} flex flex-col`}>
        {/* Top header bar (only while picking a project). */}
        {picking && (
          <div className="text-white" style={{ background: 'linear-gradient(to right, #172554 0%, #1e40af 50%, #4c1d95 100%)' }}>
            {/* Colored header bar: brand (in a white plate so the logo stays legible) + back. */}
            <header className="w-full px-6 py-3 flex items-center gap-3">
              <div className="rounded-lg bg-white px-3 py-1.5 shadow-sm">
                {brandLogoBroken ? (
                  <span className="text-base font-bold bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-600 bg-clip-text text-transparent">
                    4sCoreASTRUM
                  </span>
                ) : (
                  <img
                    /* The full lockup — mark PLUS wordmark. The header is where the
                       product is named, so it keeps the wordmark; the floating tile
                       below uses the square mark on its own. */
                    src="/4scoreastrum-logo-full.png"
                    alt="4sCoreASTRUM"
                    onError={() => setBrandLogoBroken(true)}
                    className="h-8 w-auto max-w-[200px] object-contain"
                  />
                )}
              </div>
              <button
                type="button"
                onClick={() => { setProjectChoices(null); setProjectFilter(''); }}
                disabled={loading}
                className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-white/30 bg-white/10 px-3 py-1.5 text-sm text-white transition-colors hover:bg-white/20 disabled:opacity-50"
              >
                <i className="pi pi-arrow-left text-xs" /> Back to sign in
              </button>
            </header>

            {/* Refined divider: a hairline that fades at both edges (with a faint glow
                beneath), so it reads as an intentional separator, not a hard border. */}
            <div className="relative h-px w-full bg-gradient-to-r from-transparent via-white/45 to-transparent">
              <div className="absolute inset-x-0 top-px h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            </div>

            {/* Welcome hero band with a floating, glowing shield on the right. */}
            <div className="relative overflow-hidden px-6 pt-4 pb-10">
              <div className="max-w-7xl mx-auto relative z-10">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-200/80">Welcome</p>
                <h1 className="mt-1.5 text-3xl font-bold tracking-tight">Choose a project</h1>
                <p className="mt-1.5 text-sm text-blue-100/80">
                  This email is registered in more than one project — pick the one to sign in to.
                </p>
              </div>
              <div className="pointer-events-none absolute right-10 top-1/2 -translate-y-1/2 hidden sm:block lg-float">
                <div className="relative">
                  <span className="absolute inset-0 -inset-x-4 -inset-y-2 rounded-full bg-white/20 blur-3xl" />
                  {/* No plate. The art is a cut-out with a transparent background,
                      so it sits straight on the band — a white card behind it would
                      put back the very background the cut-out removed. The glow
                      above is what separates it from the gradient. */}
                  {brandLogoBroken ? (
                    <div className="relative w-24 h-24 rounded-2xl flex items-center justify-center bg-white shadow-xl ring-1 ring-white/40 p-1.5">
                      <img src={projectsIcon} alt="" className="h-12 w-auto object-contain" />
                    </div>
                  ) : (
                    <img
                      src="/4scoreastrum-logo.png"
                      alt="4sCoreASTRUM"
                      className="relative h-24 w-auto object-contain drop-shadow-2xl"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        <div className={`flex-1 w-full flex justify-center px-6 ${picking ? 'items-start py-8' : 'items-center py-12'}`}>
        <div className={`lg-enter w-full ${picking ? 'max-w-7xl' : 'max-w-md'}`}>
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <div className="w-14 h-14 rounded-xl overflow-hidden shadow-lg bg-white">
              <img src="/4scoreastrum-logo.png" alt="4sCoreASTRUM" className="w-full h-full object-cover"
                onError={(e) => { (e.currentTarget.style.display = 'none'); }} />
            </div>
          </div>

          {!fullBleed && (
            <div className="mb-8">
              <h2 className="text-3xl font-semibold tracking-tight text-ink">
                Welcome back
              </h2>
              <p className="mt-2 text-body-sm text-ink-muted">
                Enter your credentials to access the platform
              </p>
            </div>
          )}

          {error && (
            <Message
              severity="error"
              text={error}
              className="mb-6 w-full"
            />
          )}

          {signingInto ? (
            <div className="flex flex-col items-center justify-center py-16 gap-7 text-center lg-enter" data-testid="Login.SigningIn">
              {/* The chosen project's image, shown cleanly over a soft pulsing glow —
                  no hard border/ring around it. */}
              <div className="relative flex items-center justify-center">
                <span className="absolute w-32 h-32 rounded-full bg-blue-400/20 blur-2xl animate-pulse" />
                <div className="relative w-32 h-32 flex items-center justify-center animate-pulse">
                  <ProjectImage
                    imageUrl={signingMeta?.imageUrl}
                    name={signingInto.name}
                    appCode={signingInto.appCode}
                    className="w-full h-full rounded-2xl"
                    initialSize={48}
                  />
                </div>
              </div>
              <div>
                <p className="text-lg font-semibold text-ink flex items-center justify-center gap-2">
                  <i className="pi pi-spin pi-spinner text-blue-600" />
                  Signing in to {signingInto.name}
                </p>
                <p className="text-sm text-ink-muted mt-1.5">
                  Loading your roles &amp; permissions…
                </p>
              </div>
            </div>
          ) : projectChoices ? (
            (() => {
              // Show a search box + tighter grid once there are many projects.
              const MANY = 6;
              const showSearch = projectChoices.length > MANY;
              const q = projectFilter.trim().toLowerCase();
              const visible = q
                ? projectChoices.filter((p) => {
                    const meta = p.appCode ? publicProjects.find((pp) => pp.appCode === p.appCode) : undefined;
                    return (
                      p.name.toLowerCase().includes(q) ||
                      (p.appCode || '').toLowerCase().includes(q) ||
                      (meta?.description || '').toLowerCase().includes(q)
                    );
                  })
                : projectChoices;
              return (
                <div className="space-y-6" data-testid="Login.ProjectChooser">
                  {/* Heading + brand live in the colored hero header above. */}
                  {showSearch && (
                    <div className="relative max-w-md mx-auto">
                      <i className="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400" />
                      <input
                        type="text"
                        value={projectFilter}
                        onChange={(e) => setProjectFilter(e.target.value)}
                        placeholder="Search projects…"
                        autoFocus
                        className="ds-card w-full pl-8 pr-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-blue-400"
                        data-testid="Login.ProjectSearch"
                      />
                    </div>
                  )}

                  {/* Dashboard-style cards filling the width — cards stretch to fill the
                      row (auto-fit), so a few projects fill the page instead of floating. */}
                  <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))' }}>
                    {visible.map((p, idx) => {
                      const meta = p.appCode
                        ? publicProjects.find((pp) => pp.appCode === p.appCode)
                        : undefined;
                      return (
                        <button
                          key={p.appId || p.appCode || p.name}
                          type="button"
                          onClick={() => chooseProject(p)}
                          disabled={loading}
                          className="ds-card group w-full flex items-center gap-5 text-left p-6 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50/40 dark:hover:bg-blue-900/10 hover:shadow-md disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-400"
                          data-testid="Login.ProjectChoice"
                        >
                          {/* Clear logo on a white tile. */}
                          <div className="w-24 h-24 shrink-0 rounded-2xl overflow-hidden ring-1 ring-black/5 bg-white dark:bg-gray-800 flex items-center justify-center">
                            <ProjectImage
                              imageUrl={meta?.imageUrl}
                              name={p.name}
                              appCode={p.appCode}
                              fit="contain"
                              className="w-full h-full"
                              initialSize={38}
                              fallbackGradient={ICON_GRADIENTS[idx % ICON_GRADIENTS.length]}
                            />
                          </div>

                          {/* Name + description (shown clearly). */}
                          <div className="flex-1 min-w-0">
                            <span className="block font-semibold text-lg text-ink truncate">{p.name}</span>
                            {meta?.description && (
                              <p className="mt-1 text-sm text-ink-muted line-clamp-3 leading-relaxed">{meta.description}</p>
                            )}
                          </div>

                          {/* Simple, elegant select affordance — a chevron that colors and
                              nudges on hover (replaces the "Sign in" button). */}
                          <i className="pi pi-chevron-right shrink-0 text-lg text-gray-300 dark:text-gray-600 group-hover:text-blue-600 dark:group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all" />
                        </button>
                      );
                    })}
                    {visible.length === 0 && (
                      <div className="w-full text-center py-8 text-sm text-ink-muted">
                        No projects match “{projectFilter}”.
                      </div>
                    )}
                  </div>
                </div>
              );
            })()
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="loginId" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                User ID or Email
              </label>
              <Input
                id="loginId"
                name="loginId"
                placeholder="Enter your User ID or Email"
                value={loginId}
                onChange={(e: any) => setLoginId(typeof e === 'string' ? e : e.target.value)}
                required
                autoFocus
                style={{ width: '100%', fontSize: '1rem' }}
                testId="Login.Input.LoginId"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="mpin" className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  MPIN
                </label>
                <a href="#" className="text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 transition-colors">
                  Forgot MPIN?
                </a>
              </div>
              <MpinInput
                id="mpin"
                value={mpin}
                onChange={setMpin}
                length={4}
                testId="Login.Input.Mpin"
              />
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                label={loading ? 'Signing in...' : 'Sign In'}
                icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-arrow-right'}
                iconPos="right"
                disabled={loading}
                className="w-full login-submit-btn"
                style={{
                  padding: '0.9rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: 'var(--brand-gradient-br)',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 10px 24px -8px rgba(19,95,168,0.55)',
                  transition: 'transform .2s ease, box-shadow .2s ease',
                }}
                data-testid="Login.Button.Submit"
              />
            </div>
          </form>
          )}
        </div>
        </div>

        {/* Footer pinned to the bottom of the page. */}
        <div className="border-t border-line py-4 px-6 text-center">
          <p className="text-xs text-ink-muted flex items-center justify-center gap-1.5">
            <i className="pi pi-shield" style={{ fontSize: '0.7rem' }} />
            Protected by{' '}
            <span className="font-semibold bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-600 bg-clip-text text-transparent">
              4sCoreASTRUM
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
